import { HPTARDetailData, HPTARInvalidData } from "@/api/hptar";
import { cn } from "@/utils/helper";

export interface RowData {
  warnMessageDetails: MessageRowData[];
  folder: IsiCellData;
  intervalId: IsiCellData;
  clusterCode: IsiCellData;
  siteId: IsiCellData;
  locality: IsiCellData;
  prefixAddress: IsiCellData;
  stName: IsiCellData;
  stNo: IsiCellData;
  block: IsiCellData;
  floor: IsiCellData;
  rt: IsiCellData;
  rw: IsiCellData;
  postCode: IsiCellData;
  subDistrict: IsiCellData;
  village: IsiCellData;
  fiberNode: IsiCellData;
  fdtType: IsiCellData;
  latitude: IsiCellData;
  longitude: IsiCellData;
  categoryBizzpass: IsiCellData;
  addressPole: IsiCellData;
  ovUg: IsiCellData;
  houseComment: IsiCellData;
  buildingName: IsiCellData;
  tower: IsiCellData;
  aptn: IsiCellData;
  fiberNodeHfc: IsiCellData;
  idArea: IsiCellData;
  fdtCode: IsiCellData;
  fatCode: IsiCellData;
  census: IsiCellData;
  poleLat: IsiCellData;
  poleLong: IsiCellData;
  poleProvider: IsiCellData;
  poleType: IsiCellData;
  poleLine: IsiCellData;
  clampHookId: IsiCellData;
  clampHookLatitude: IsiCellData;
  clampHookLongitude: IsiCellData;
  deploymentType: IsiCellData;
  needSurvey: IsiCellData;
  statusHomepass: IsiCellData;
  reason: IsiCellData;
}

interface IsiCellData {
  value: string;
  details: MessageRowData[];
}

export interface MessageRowData {
  message?: string;
}

// Table configuration for HPTAR validation results
const HPTAR_TABLE_COLUMNS = [
  { key: "message", label: "Message" },
  { key: "folder", label: "FOLDER" },
  { key: "intervalId", label: "INTERVAL_ID" },
  { key: "clusterCode", label: "CLUSTER_CODE" },
  { key: "siteId", label: "Site ID" },
  { key: "locality", label: "Locality" },
  { key: "prefixAddress", label: "Prefix Address" },
  { key: "stName", label: "ST Name" },
  { key: "stNo", label: "ST No" },
  { key: "block", label: "Block" },
  { key: "floor", label: "Floor" },
  { key: "rt", label: "RT" },
  { key: "rw", label: "RW" },
  { key: "postCode", label: "Post Code" },
  { key: "subDistrict", label: "District" },
  { key: "village", label: "Village Name" },
  { key: "fiberNode", label: "FIBERNODE" },
  { key: "fdtType", label: "FDT_TYPE" },
  { key: "latitude", label: "Latitude" },
  { key: "longitude", label: "Longitude" },
  { key: "categoryBizzpass", label: "Category Bizzpass" },
  { key: "addressPole", label: "Address Pole/Lat" },
  { key: "ovUg", label: "OV_UG" },
  { key: "houseComment", label: "House Comment" },
  { key: "buildingName", label: "Building Name" },
  { key: "tower", label: "Tower" },
  { key: "aptn", label: "APTN" },
  { key: "fiberNodeHfc", label: "FIBER_NODE__HFC_" },
  { key: "idArea", label: "ID_Area" },
  { key: "fdtCode", label: "FDT_CODE" },
  { key: "fatCode", label: "FAT_CODE" },
  { key: "poleId", label: "Census" },
  { key: "poleLat", label: "Pole Lat" },
  { key: "poleLong", label: "Pole Long" },
  { key: "poleProvider", label: "Pole Provider (New)" },
  { key: "poleType", label: "Pole Type" },
  { key: "poleLine", label: "LINE" },
  { key: "clampHookId", label: "Clamp Hook ID" },
  { key: "clampHookLatitude", label: "Clamp Hook Latitude" },
  { key: "clampHookLongitude", label: "Clamp Hook Longitude" },
  { key: "deploymentType", label: "Deployment Type" },
  { key: "needSurvey", label: "Need Survey" },
  { key: "statusHomepass", label: "Status Homepass" },
  { key: "reason", label: "Reason" },
] as const;

// Helper function to get cell styling based on validation errors
function getCellClassName(hasErrors: boolean): string {
  const baseClasses =
    "w-64 whitespace-normal p-4 first:sticky first:left-0 first:z-10 bg-white first:border-r first:shadow-sm";
  return hasErrors ? cn(`${baseClasses}`, "bg-red-500") : baseClasses;
}

// Helper function to render table header
export function renderTableHeader() {
  return (
    <thead className="sticky top-0 z-20 bg-slate-100 shadow-sm">
      <tr className="flex border-b text-left text-sm ">
        {HPTAR_TABLE_COLUMNS.map((column) => (
          <th
            key={column.key}
            className="w-64 whitespace-nowrap bg-slate-100 p-4 font-medium first:sticky first:left-0 first:z-20 first:border-r first:shadow-sm"
          >
            {column.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

// Helper function to render a single table cell
export function renderTableCell(
  cellData: IsiCellData | undefined,
  isMessageCell: boolean = false,
  messageData?: HPTARDetailData[],
) {
  if (isMessageCell && messageData) {
    return (
      <td className="w-64 whitespace-normal bg-white p-4 first:sticky first:left-0 first:z-10 first:border-r first:shadow-sm">
        {messageData.map((detailData) => detailData.message).join(", ")}
      </td>
    );
  }

  const hasErrors = Boolean(cellData?.details?.length);
  return (
    <td className={getCellClassName(hasErrors)}>{cellData?.value ?? "-"}</td>
  );
}

// Helper function to render a single table row
export function renderTableRow(row: HPTARInvalidData, index: number) {
  return (
    <tr key={index} className="flex text-sm">
      {HPTAR_TABLE_COLUMNS.map((column) => {
        // Handle message column specially
        if (column.key === "message") {
          return renderTableCell(undefined, true, row.message);
        }
        // Dynamically access row property using column key
        return renderTableCell(
          row[column.key as keyof HPTARInvalidData] as IsiCellData,
        );
      })}
    </tr>
  );
}
